import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import { ApiError } from "@/api/fetchApi";
import { keys, useSaveQuestionnaire } from "@/api/queries";
import { useAuth } from "@/auth/AuthProvider";
import { errorText } from "@/components/ErrorPanel";
import {
  type FhsaChoice,
  fhsaChoiceEdits,
  FhsaTransferDialog,
  fhsaTransferErrors,
} from "@/components/FhsaTransferDialog";
import { useToast } from "@/components/Toast";
import type { FamilyDocument } from "@/generated/backend/v1/family";
import type { Questionnaire } from "@/generated/core/v1/questionnaire";
import type { FieldError } from "@/generated/core/v1/value_source";

/** Update Data's first-time home buyer boxes, under "What you owe" (`FirstTimeBuyerChecks`). */
export const FIRST_TIME_BOXES = "ud-first-time";
/**
 * Where an FHSA whose holder isn't a first-time home buyer is fixed: Update Data's "What you owe",
 * opened and scrolled to the boxes. For the FHSA dialog's "Let me fix my data" and the FHSA warnings
 * (Home's and Sharpen's data checks, the check-in).
 */
export const FIX_FIRST_TIME_BUYER_URL = `/app/update-data?open=debts&show=${FIRST_TIME_BOXES}`;

export type SaveState = {
  error?: string;
  fieldErrors: FieldError[];
  /** A 409: the household changed elsewhere; the newer version is now shown. */
  conflict: boolean;
};

type FhsaAsk = {
  errors: FieldError[];
  questionnaire: Questionnaire;
  success: string | null;
  resolve: (saved: boolean) => void;
};

export type FhsaControls = {
  fhsa: FhsaAsk | null;
  fhsaPending: boolean;
  chooseFhsa: (choice: FhsaChoice) => void;
  fixFhsa: () => void;
  closeFhsa: () => void;
  nameOf: (personUuid: string) => string | undefined;
};

/**
 * Partial saves of the family (Update Data, Sharpen, Edit priorities): POST the questionnaire with
 * the version the page was showing. A 409 loads the newer version into the cache (the page
 * re-renders with it) and asks the user to check and save again; a 422 returns the engine's errors
 * by field path. A 422 that is only an unconfirmed FHSA move opens the FHSA dialog (rendered by
 * `SaveFeedback`): `run` then resolves when the user picks (true once the resent save succeeds).
 */
export function useFamilySave(doc: FamilyDocument | undefined) {
  const save = useSaveQuestionnaire();
  const client = useQueryClient();
  const uid = useAuth().user?.uid;
  const toast = useToast();
  const navigate = useNavigate();
  const [state, setState] = useState<SaveState>({ fieldErrors: [], conflict: false });
  const [fhsa, setFhsa] = useState<FhsaAsk | null>(null);

  /** One POST: "ok", "fhsa" (the dialog's case, with its errors), or "error" (state set). */
  const attempt = useCallback(
    async (
      questionnaire: Questionnaire,
      success: string | null,
    ): Promise<{ ok: true } | { ok: false; fhsa?: FieldError[] }> => {
      setState({ fieldErrors: [], conflict: false });
      try {
        await save.mutateAsync({ questionnaire, expectedVersion: doc?.version ?? 0 });
        if (success) toast(success); // null: the page says it (Update Data's save bar)
        return { ok: true };
      } catch (e) {
        const coded = fhsaTransferErrors(e);
        if (coded) return { ok: false, fhsa: coded };
        if (e instanceof ApiError && e.isConflict) {
          if (e.conflict && uid) client.setQueryData(keys.family(uid), e.conflict);
          setState({
            fieldErrors: [],
            conflict: true,
            error:
              "Your household was changed somewhere else, so we’ve loaded the newer version. Check your change against it and save again.",
          });
        } else if (e instanceof ApiError && e.isValidation) {
          // an error not tied to a field is the engine's own wording, not something to show
          const error = e.fieldErrors.length
            ? e.message
            : "We couldn’t save this change. Try again, and contact us if it keeps happening.";
          setState({ fieldErrors: e.fieldErrors, conflict: false, error });
        } else {
          setState({ fieldErrors: [], conflict: false, error: errorText(e) });
        }
        return { ok: false };
      }
    },
    [save, doc?.version, toast, client, uid],
  );

  const run = useCallback(
    async (
      questionnaire: Questionnaire,
      success: string | null = "Saved. Abi is rebuilding your plan now.",
    ) => {
      const res = await attempt(questionnaire, success);
      if (res.ok) return true;
      const errors = res.fhsa;
      if (!errors) return false;
      return new Promise<boolean>((resolve) =>
        setFhsa({ errors, questionnaire, success, resolve }),
      );
    },
    [attempt],
  );

  const chooseFhsa = useCallback(
    async (choice: FhsaChoice) => {
      const ask = fhsa;
      if (!ask) return;
      const q: Questionnaire = {
        ...ask.questionnaire,
        fieldEdits: [...ask.questionnaire.fieldEdits, ...fhsaChoiceEdits(ask.errors, choice)],
      };
      const res = await attempt(q, ask.success);
      if (!res.ok && res.fhsa) {
        // still refused (another FHSA): ask again about what's left
        setFhsa({ ...ask, errors: res.fhsa, questionnaire: q });
        return;
      }
      setFhsa(null);
      ask.resolve(res.ok);
    },
    [attempt, fhsa],
  );

  const closeFhsa = useCallback(() => {
    setFhsa(null);
    fhsa?.resolve(false);
  }, [fhsa]);

  const fixFhsa = useCallback(() => {
    closeFhsa();
    navigate(FIX_FIRST_TIME_BUYER_URL);
  }, [closeFhsa, navigate]);

  const nameOf = useCallback(
    (personUuid: string) =>
      doc?.family?.persons.find((p) => p.uuid === personUuid)?.givenName || undefined,
    [doc],
  );

  return {
    run,
    pending: save.isPending,
    ...state,
    fhsa,
    fhsaPending: save.isPending && !!fhsa,
    chooseFhsa: (choice: FhsaChoice) => void chooseFhsa(choice),
    fixFhsa,
    closeFhsa,
    nameOf,
  };
}

/** The save's error (and the engine's field errors) under a form, and the FHSA dialog. */
export function SaveFeedback({ state }: { state: SaveState & Partial<FhsaControls> }) {
  return (
    <>
      {state.fhsa && state.chooseFhsa && state.fixFhsa && state.closeFhsa && (
        <FhsaTransferDialog
          errors={state.fhsa.errors}
          nameOf={state.nameOf ?? (() => undefined)}
          pending={!!state.fhsaPending}
          onChoose={state.chooseFhsa}
          onFix={state.fixFhsa}
          onClose={state.closeFhsa}
        />
      )}
      {(state.error || state.fieldErrors.length > 0) && (
        <div className="wp-savefb" role="alert" data-testid="save-error">
          {state.error && <p className="wp-form-err">{state.error}</p>}
          {state.fieldErrors.length > 0 && (
            <ul>
              {state.fieldErrors.map((f) => (
                <li key={`${f.path}:${f.message}`}>{f.message}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  );
}
